import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import {
  Box,
  Stack,
  Text,
  TextLink,
  Button,
  IconRocket,
  Inline,
  useDialogControllers,
  Table,
  Skeleton,
  Badge,
  Spread,
  Alert,
  Dialog,
  ResponsiveRender,
  useResponsiveValue,
  Heading,
  ContentBlock
} from '@machobear/design-system'
import { format, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { enUS, fr, zhCN } from 'date-fns/locale'
import { useCallback, useMemo, useState } from 'react'
import DashboardHeaderTitle from '@/components/header/dashboard-header-title'
import { Breadcrumbs } from '@/components/breadcrumbs'
import PageTitle from '@/components/page-title'
import { FilterTextWrapper } from '@/components/search-filter'

import { TabMenu } from '@/components/tabs'
import { handleTabByKeyboard } from '@/utils/handleTabByKeyboard'
import { useGetSponsoredListing } from '@/hooks/use-sponsored-listing'

import { showPreviewModal } from '@/features/SponsoredListing/modals/preview-modal'
import useObjectStorage from '@/hooks/use-objectstorage'
import SponsorDialogs from '@/features/SponsoredListing/components/sponsor-dialogs'

import { useSponsoredListingCreditInformation } from '@/hooks/use-block-and-pay'
import { SponsoredListingProvider } from '@/features/SponsoredListing/modals/sponsor-wizard'
import useProfile from '@/hooks/use-profile'
import config from '@/config'
import { type SponsoredListingDetail } from '@/features/SponsoredListing/types'
import NoListinsIllustration from './illustration/no-listings-illustration.svg?react'
import ListingSponsoredList from './ListingSponsoredList'

const { WEB_URL, APP_PATH } = config

type Listing = {
  listing_id: string
  unit_number: string | null
  full_street_name: string
}

export const BADGE_TONE_MAP = {
  open: 'positive' as const,
  active: 'caution' as const,
  history: 'neutral' as const
}

const PRODUCT_MAP = {
  'seven-days-sponsored-listing-package': '7-Days Sponsored Listing',
  'fourteen-days-sponsored-listing-package': '14-Days Sponsored Listing',
  'thirty-days-sponsored-listing-package': '28-Days Sponsored Listing'
}

const STATUS_ORDER = ['active', 'open', 'history']
const STATUS_TAG_MAP = {
  active: 'Active',
  open: 'Upcoming',
  history: 'History'
}

export const TIME_TRANSLATION_MAP = {
  en: enUS,
  zh: zhCN,
  fr
}

const SponsoredListingDashboard = ({ listing }: { listing: Listing }) => {
  const { t, i18n } = useTranslation()
  const listingId = listing?.listing_id

  const profileData = useProfile()
  const { data: sponsoredListing, isLoading } = useGetSponsoredListing(listingId)

  const { availableCredits, isLoading: isLoadingCredits } = useSponsoredListingCreditInformation()

  const [currentTab, setCurrentTab] = useState('all')
  const [sortBy, setSortBy] = useState<{ id: string; desc?: boolean } | null>(null)

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const { visible, showModal, closeModal } = useDialogControllers({
    billingUser: false,
    sponsoredListing: false,
    nextSteps: false,
    status: false
  })

  const [dismissedAlert, setDismissedAlert] = useObjectStorage('dismissed_alert', {})

  const handleBoostListing = () => {
    if (profileData?.billing_user_valid) {
      showModal('sponsoredListing')()
    } else {
      showModal('billingUser')()
    }
  }

  const allData = useMemo(() => {
    if (!sponsoredListing?.sponsored_listings) return []

    const sponsoredListingArrData: SponsoredListingDetail[] = Object.values(
      sponsoredListing.sponsored_listings
    )

    return sponsoredListingArrData.sort((a, b) => {
      if (!sortBy) {
        const statusComparison =
          STATUS_ORDER.indexOf(a.state_machine) - STATUS_ORDER.indexOf(b.state_machine)
        return (
          statusComparison ||
          new Date(b.planned_start_date).getTime() - new Date(a.planned_start_date).getTime()
        )
      }

      if (sortBy.id === 'dateRange') {
        const dateA = new Date(a.planned_start_date).getTime()
        const dateB = new Date(b.planned_start_date).getTime()
        return sortBy.desc ? dateB - dateA : dateA - dateB
      }

      if (sortBy.id === 'plan_name') {
        const getDays = (productName: string) => {
          const numberMatch = productName.match(/(\d+)-Days/)
          return numberMatch ? parseInt(numberMatch[1]) : 0
        }
        const productA = getDays(PRODUCT_MAP[a.plan_name as keyof typeof PRODUCT_MAP])
        const productB = getDays(PRODUCT_MAP[b.plan_name as keyof typeof PRODUCT_MAP])
        const comparison = productA - productB
        return sortBy.desc ? comparison : -comparison
      }

      if (sortBy.id === 'state_machine') {
        const statusA = STATUS_TAG_MAP[a.state_machine as keyof typeof STATUS_TAG_MAP]
        const statusB = STATUS_TAG_MAP[b.state_machine as keyof typeof STATUS_TAG_MAP]
        const comparison = statusA.localeCompare(statusB)
        return sortBy.desc ? comparison : -comparison
      }

      return 0
    })
  }, [sponsoredListing, sortBy])

  const activeData = useMemo(() => {
    return allData.filter(data => data.state_machine !== 'history')
  }, [allData])

  const historyData = useMemo(() => {
    return allData.filter(data => data.state_machine === 'history')
  }, [allData])

  const dataMap: { [key: string]: SponsoredListingDetail[] } = {
    all: allData,
    active: activeData,
    history: historyData
  }

  const menuItems = [
    {
      key: 'all',
      onClick: () => setCurrentTab('all'),
      isActive: currentTab === 'all',
      children: (
        <FilterTextWrapper>
          {t('l.dashboard.listings.label.all', 'All')} <span>({allData.length})</span>
        </FilterTextWrapper>
      )
    },
    {
      key: 'active',
      onClick: () => setCurrentTab('active'),
      isActive: currentTab === 'active',
      children: (
        <FilterTextWrapper>
          {t('l.dashboard.listings.label.active', 'Active')} <span>({activeData.length})</span>
        </FilterTextWrapper>
      )
    },
    {
      key: 'history',
      onClick: () => setCurrentTab('history'),
      isActive: currentTab === 'history',
      children: (
        <FilterTextWrapper>
          {t('l.dashboard.listings.label.history', 'History')} <span>({historyData.length})</span>
        </FilterTextWrapper>
      )
    }
  ]

  const isLoadingTableData = isLoading || !listingId

  const columnDefinition = [
    {
      accessor: 'plan_name',
      Header: isLoadingTableData ? (
        <Skeleton.Shape width={160} height={32} />
      ) : (
        t('l.credit_usage.product.product.title', 'Product')
      ),
      gridWidth: 'fill-available',
      Cell: ({ value }: { value?: string }) => {
        if (isLoadingTableData) {
          return <Skeleton.Shape width={160} height={32} />
        }
        const duration = value?.replace('-days-sponsored-listing-package', '')
        const days =
          duration === 'seven'
            ? t('l.dashboard.sponsored_listings.duration.week')
            : duration === 'fourteen'
              ? t('l.dashboard.sponsored_listings.duration.two_weeks', '14 Days')
              : t('l.dashboard.sponsored_listings.duration.month', '28 Days')

        return (
          <Text size="small">{`${days} ${t('common.sponsored_listings', 'Sponsored Listing')}`}</Text>
        )
      }
    },
    {
      accessor: 'dateRange',
      Header: isLoadingTableData ? (
        <Skeleton.Shape width={160} height={32} />
      ) : (
        t('l.dashboard.table.data_range', 'Date range')
      ),
      gridWidth: 'fill-available',
      // DS Table is Cell type is CellProps<P{{}, any}>. The first param is the value and the second is the row.
      Cell: ({ row }: { row: { original: any } }) => {
        if (isLoadingTableData) {
          return <Skeleton.Shape width={160} height={32} />
        }

        const formatDate = (date: string) =>
          format(parseISO(date), 'MMM d, yyyy', {
            locale: TIME_TRANSLATION_MAP[i18n?.language as keyof typeof TIME_TRANSLATION_MAP]
          })
        const activeStartDate = formatDate(row.original.planned_start_date)
        const activeEndDate = formatDate(row.original.planned_end_date)

        return (
          <Text size="small">
            {activeStartDate} - {activeEndDate}
          </Text>
        )
      }
    },
    {
      accessor: 'time',
      Header: isLoadingTableData ? (
        <Skeleton.Shape width={160} height={32} />
      ) : (
        t('l.dashboard.table.time', 'Time')
      ),
      gridWidth: 'fill-available',
      disableSortBy: true,
      // DS Table is Cell type is CellProps<P{{}, any}>. The first param is the value and the second is the row.
      Cell: ({ row }: { row: { original: any } }) => {
        if (isLoadingTableData) {
          return <Skeleton.Shape width={160} height={32} />
        }
        const formattedTime = formatInTimeZone(
          parseISO(row.original.planned_start_date),
          row.original.user_timezone,
          'h:mm aa zzz',
          { locale: TIME_TRANSLATION_MAP[i18n.language as keyof typeof TIME_TRANSLATION_MAP] }
        )
        return <Text size="small">{formattedTime}</Text>
      }
    },
    {
      accessor: 'state_machine',
      Header: isLoadingTableData ? (
        <Skeleton.Shape width={160} height={32} />
      ) : (
        t('l.dashboard.table.status', 'Status')
      ),
      gridWidth: 'fill-available',
      hint: isLoadingTableData
        ? null
        : {
            onClick: (e: Event) => {
              e.stopPropagation()
              showModal('status')()
            }
          },

      Cell: ({ value }: { value: string }) => {
        if (isLoadingTableData) {
          return <Skeleton.Shape width={160} height={32} />
        }
        const displayText =
          value === 'open'
            ? t('l.listing.dashboard.upcoming.badge.title', 'Upcoming')
            : value === 'active'
              ? t('l.listing.dashboard.active.badge.title', 'Active')
              : t('l.listing.dashboard.history.badge.title', 'History')

        return (
          <Badge tone={BADGE_TONE_MAP[value as keyof typeof BADGE_TONE_MAP]}>{displayText}</Badge>
        )
      }
    }
  ]

  const statusColumnDefinition = [
    {
      accessor: 'status',
      Header: t('l.dashboard.table.status', 'Status'),
      gridWidth: 'fill-available',
      disableSortBy: true,
      Cell: ({ value }: { value?: string }) => <Text>{value}</Text>
    },
    {
      accessor: 'description',
      Header: t('l.dashboard.listings.label.description', 'Description'),
      gridWidth: 'fill-available',
      disableSortBy: true,
      Cell: ({ value }: { value?: string }) => <Text>{value}</Text>
    }
  ]

  const statusData = [
    {
      status: t('l.listing.dashboard.active.badge.title', 'Active'),
      description: t(
        'l.listing.dashboard.active.badge.description',
        'Your listing is live and currently being sponsored.'
      )
    },
    {
      status: t('l.listing.dashboard.upcoming.badge.title', 'Upcoming'),
      description: t(
        'l.listing.dashboard.upcoming.badge.description',
        'Scheduled to start soon - get ready for more views.'
      )
    },
    {
      status: t('l.listing.dashboard.history.badge.title', 'History'),
      description: t(
        'l.listing.dashboard.history.badge.description',
        'Past sponsorships that have ended.'
      )
    }
  ]

  const handleSort = useCallback((sortArr?: { id: string; desc?: boolean }[]) => {
    setSortBy(sortArr?.[0] ?? null)
  }, [])

  const listingAddress =
    (listing?.unit_number ? `${listing?.unit_number} - ` : '') + (listing?.full_street_name || '')

  return (
    <Box
      background="white"
      height="full"
      position="relative"
      padding={{ mobile: 'none', tablet: 'xlarge' }}
    >
      <Helmet>
        <title>{t('l.sse.sponsored_listings.title', 'Sponsored Listings')}</title>
      </Helmet>

      <ResponsiveRender below="tablet">
        <PageTitle>
          <DashboardHeaderTitle>
            {t('l.sse.sponsored_listings.title', 'Sponsored Listings')}
          </DashboardHeaderTitle>
        </PageTitle>
      </ResponsiveRender>

      <ResponsiveRender above="mobile">
        <PageTitle>
          <Breadcrumbs
            items={[
              { to: '/listings', label: t('l.listing.view.all_listings.link', 'All Listings') },
              { to: `/listing/${listingId}`, label: listingAddress },
              { label: t('l.sse.sponsored_listings.title', 'Sponsored Listings') }
            ]}
          />
        </PageTitle>
      </ResponsiveRender>

      <ContentBlock width="medium">
        <Box
          maxWidth="medium"
          width="full"
          position="relative"
          style={isMobile ? { minHeight: 'calc(100vh - 53px)' } : {}}
        >
          <ResponsiveRender above="mobile">
            <Stack align="left" space="xxlarge">
              <Stack align="left" space="gutter">
                <Heading level="4">
                  {t('l.dashboard.sponsored_listings.title', 'Sponsored Listings')}
                </Heading>

                <Box maxWidth="small">
                  <Text>
                    {t(
                      'l.dashboard.sponsored_listings.description',
                      "Get in front of qualified tenants and boost your listing's exposure with front & centre placement."
                    )}{' '}
                    <TextLink
                      href={`${WEB_URL}/landlords/sponsored-listings`}
                      showVisited
                      target="_blank"
                      weight="regular"
                    >
                      {t('l.dashboard.sponsored_listings.learn_more', 'Learn more')}
                    </TextLink>
                  </Text>
                </Box>
              </Stack>
              <Inline space="small" alignY="center">
                <Button icon={<IconRocket />} onClick={handleBoostListing}>
                  {t('common.sponsor', 'Boost listing')}
                </Button>
                <Button variant="transparent" onClick={() => showPreviewModal({ listingId })}>
                  {t('l.dashboard.sponsored_listings.preview_my_ad', 'Preview my ad')}
                </Button>
              </Inline>
              {!dismissedAlert?.sl_alert?.includes(profileData?.id) && (
                <Alert
                  aria-label="Sponsored listings info alert"
                  tone="promote"
                  onClose={() => {
                    const currentAlerts = dismissedAlert?.sl_alert || []
                    setDismissedAlert({
                      ...dismissedAlert,
                      sl_alert: [...currentAlerts, profileData?.id]
                    })
                  }}
                  closeLabel="Close info alert"
                >
                  <Trans t={t} i18nKey="l.dashboard.sponsored_listings.alert">
                    <Text weight="medium" tone="promote" size="small">
                      Want to speed up your workflow?{' '}
                      <TextLink href={`/usage-credits/products/rental-advertising`} target="_blank">
                        Get credits{' '}
                      </TextLink>
                    </Text>
                  </Trans>
                </Alert>
              )}

              <Stack space="gutter">
                <Spread space="gutter" alignY="center">
                  <Box
                    tabIndex={0}
                    onKeyDown={e => handleTabByKeyboard(e, menuItems, currentTab, setCurrentTab)}
                    role="tabpanel"
                  >
                    <TabMenu
                      options={menuItems}
                      theme={'secondary'}
                      size="medium"
                      tabIndex={-1}
                      aria-label="Sponsored listing tabs"
                    />
                  </Box>

                  {isLoadingCredits ? (
                    <Skeleton.Shape width={150} height={25} />
                  ) : (
                    <Box
                      onClick={() =>
                        window.open(`${APP_PATH}/usage-credits/products/my-products`, '_blank')
                      }
                      role="button"
                      cursor="pointer"
                      background="promoteLight"
                      padding="xxsmall"
                      borderRadius="small"
                    >
                      <Text size="xsmall" weight="medium">
                        {t(
                          'l.listing.sponsored_listing.modal.available_days_title',
                          `Available Credits: {{ value }} day{{ plural }}`,
                          {
                            value: availableCredits,
                            plural: availableCredits > 1 ? 's' : ''
                          }
                        )}
                      </Text>
                    </Box>
                  )}
                </Spread>

                <Box
                  style={{ maxHeight: '464px' }}
                  id={`tabpanel-${currentTab}`}
                  aria-labelledby={`tab-${currentTab}`}
                  overflow="auto"
                >
                  <Table
                    data={
                      isLoadingTableData
                        ? Array.from({ length: 5 }, () => ({}))
                        : dataMap[currentTab]
                    }
                    columns={columnDefinition}
                    noResultsTitle={t(
                      'l.dashboard.sponsored_listings.no_results.title',
                      'Boost your listing to get started'
                    )}
                    noResultsDescription=""
                    illustration={NoListinsIllustration}
                    action={{
                      label: t('l.dashboard.sponsored_listings.start_boosting', 'Start boosting'),
                      onClick: handleBoostListing
                    }}
                    itemsSelectedText=""
                    clearAllText=""
                    onSort={handleSort}
                  />
                  <Dialog
                    id="status-dialog"
                    open={visible.status}
                    onClose={closeModal('status')}
                    title={t('l.dashboard.table.status', 'Status')}
                    description={
                      <Text>
                        {t(
                          'l.dashboard.sponsored_listings.status_description',
                          'Please see the following for more information about each status.'
                        )}
                      </Text>
                    }
                  >
                    <Table
                      data={statusData}
                      columns={statusColumnDefinition}
                      itemsSelectedText=""
                      clearAllText=""
                    />
                  </Dialog>
                </Box>
              </Stack>
            </Stack>
          </ResponsiveRender>

          <ResponsiveRender below="tablet">
            <Box
              position="sticky"
              zIndex={1}
              background="white"
              paddingBottom="small"
              style={{ top: '54px' }}
            >
              <Box paddingX="small" style={{ width: '264px' }}>
                <TabMenu options={menuItems} theme={'secondary'} size="medium" tabIndex={-1} />
              </Box>
            </Box>
            <Box overflow="scroll" style={{ height: 'calc(100vh - 101px)' }}>
              <ListingSponsoredList
                data={dataMap[currentTab]}
                isLoading={isLoadingTableData}
                handleBoostListing={handleBoostListing}
              />
            </Box>
          </ResponsiveRender>
        </Box>
      </ContentBlock>

      <ResponsiveRender below="tablet">
        <Box
          position="absolute"
          bottom={0}
          background="white"
          width="full"
          padding="xsmall"
          boxShadow="borderNeutralLight"
        >
          <Stack align="center" space="xsmall">
            <Button icon={<IconRocket />} onClick={handleBoostListing}>
              {t('common.sponsor', 'Boost listing')}
            </Button>
            {isLoadingCredits ? (
              <Skeleton.Text lineCount={1} />
            ) : (
              <Text size="small">
                <TextLink
                  href={`/usage-credits/products/my-products`}
                  target="_blank"
                  weight="regular"
                >
                  {t(
                    'l.listing.sponsored_listing.modal.available_days_title',
                    `Available Credits: {{ value }} day{{ plural }}`,
                    {
                      value: availableCredits,
                      plural: availableCredits > 1 ? 's' : ''
                    }
                  )}
                </TextLink>
              </Text>
            )}
          </Stack>
        </Box>
      </ResponsiveRender>
      <SponsoredListingProvider initialStep={0} initialValues={{}} isCreateListing={false}>
        <SponsorDialogs
          listingId={listingId}
          visible={visible}
          showModal={showModal}
          closeModal={closeModal}
        />
      </SponsoredListingProvider>
    </Box>
  )
}

export default SponsoredListingDashboard
